import React, { useMemo, useState } from 'react'
import {
  Alert,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  TextField,
} from '@mui/material'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDTypography from 'components/MDTypography'
import CrudService from 'services/cruds-service'
import { adaptIntakeDraft } from './aiPrefillAdapter'
import { applyGoogleCandidate, findAddressCandidates } from './geocodeAddress'

const ALLOWED_FILE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif'])
const MAX_FILES = 5
const MAX_FILE_BYTES = 10 * 1024 * 1024

const ENTITY_LABELS = {
  name: 'Customer name',
  first_name: 'First name',
  last_name: 'Last name',
  company_id: 'Company',
  entity_type_id: 'Entity type',
  status_type_id: 'Status',
}

// Maps backend codes to user-safe text; raw backend/model text can contain customer data.
function messageFromError(error) {
  const code = error?.error
  const requestId = error?.request_id ? ` (request ${error.request_id})` : ''
  if (code === 'RATE_LIMITED') return 'Too many AI requests. Wait a minute and try again.'
  if (code === 'INVALID_MODEL_RESPONSE') return `The notes could not be turned into a draft. Try adding more detail${requestId}.`
  if (code === 'PARSE_INTAKE_FAILED') return `AI extraction failed. Please try again${requestId}.`
  if (code === 'LIMIT_FILE_SIZE') return 'Each image must be 10 MB or smaller.'
  if (error?.code === 'ERR_NETWORK' || error?.code === 'ECONNABORTED') return 'Network problem. Your input was kept. Try again.'
  if (error?.errors?.[0]?.status === '403' || code === 'FORBIDDEN') return 'You do not have permission to use AI Prefill.'
  return `Unable to generate a draft. Your input was kept${requestId}.`
}

function labelFor(field, value, dropdowns) {
  const lookups = {
    company_id: [dropdowns.companies, 'company_id', 'name'],
    entity_type_id: [dropdowns.entityTypes, 'entity_type_id', 'type_name'],
    status_type_id: [dropdowns.entityStatusOptions, 'status_type_id', 'status_name'],
  }
  const lookup = lookups[field]
  if (!lookup) return value
  const [options, key, labelKey] = lookup
  const match = (options || []).find((option) => String(option?.[key]) === String(value))
  return match?.[labelKey] ?? match?.name ?? value
}

export default function AiPrefillDialog({ open, onClose, onApply, currentState, dropdowns }) {
  const [text, setText] = useState('')
  const [files, setFiles] = useState([])
  const [draft, setDraft] = useState(null)
  const [selection, setSelection] = useState(null)
  const [geocoding, setGeocoding] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const canGenerate = useMemo(
    () => !loading && (text.trim().length > 0 || files.length > 0),
    [loading, text, files.length],
  )

  const toggle = (group, key, checked) =>
    setSelection((previous) => ({ ...previous, [group]: { ...previous[group], [key]: checked } }))

  const handleFiles = (event) => {
    const picked = Array.from(event.target.files || [])
    event.target.value = ''
    const next = [...files, ...picked]
    if (next.length > MAX_FILES) {
      setError(`Upload no more than ${MAX_FILES} images.`)
      return
    }
    if (next.some((file) => !ALLOWED_FILE_TYPES.has(file.type) || file.size > MAX_FILE_BYTES)) {
      setError('Images must be JPEG, PNG, WebP, or GIF and no larger than 10 MB each.')
      return
    }
    setError('')
    setFiles(next)
  }

  const handleGenerate = async () => {
    setLoading(true)
    setError('')
    setDraft(null)
    try {
      const response = await CrudService.parseOnboardingIntake({
        text: text.trim(),
        companyId: currentState.entity.company_id,
        files,
      })
      const nextDraft = adaptIntakeDraft(response, dropdowns)
      const nextGeocoding = {}

      const verifiedAddresses = await Promise.all(
        nextDraft.addresses.map(async (address) => {
          try {
            const candidates = await findAddressCandidates(address)
            const exact = candidates.filter((candidate) => !candidate.partialMatch)
            if (exact.length === 1) {
              nextGeocoding[address.id] = { status: 'verified', candidates, selectedPlaceId: exact[0].placeId }
              return applyGoogleCandidate(address, exact[0])
            }
            nextGeocoding[address.id] = {
              status: candidates.length ? 'needs-confirmation' : 'failed',
              candidates,
              selectedPlaceId: '',
            }
          } catch (geocodeError) {
            nextGeocoding[address.id] = { status: 'failed', candidates: [], selectedPlaceId: '' }
          }
          return address
        }),
      )
      nextDraft.addresses = verifiedAddresses

      setDraft(nextDraft)
      setGeocoding(nextGeocoding)
      setSelection({
        entity: Object.fromEntries(
          Object.entries(nextDraft.entity).map(([field, value]) => [field, Boolean(value) && !currentState.entity?.[field]]),
        ),
        addresses: Object.fromEntries(
          nextDraft.addresses.map((address) => [address.id, nextGeocoding[address.id]?.status === 'verified']),
        ),
        contacts: Object.fromEntries(nextDraft.contacts.map((contact) => [contact.id, true])),
      })
    } catch (requestError) {
      setError(messageFromError(requestError))
    } finally {
      setLoading(false)
    }
  }

  const handleAddressCandidate = (addressId, placeId) => {
    const match = geocoding[addressId]?.candidates?.find((candidate) => candidate.placeId === placeId)
    if (!match) return
    setDraft((previous) => ({
      ...previous,
      addresses: previous.addresses.map((address) =>
        address.id === addressId ? applyGoogleCandidate(address, match) : address,
      ),
    }))
    setGeocoding((previous) => ({
      ...previous,
      [addressId]: { ...previous[addressId], status: 'verified', selectedPlaceId: placeId },
    }))
    toggle('addresses', addressId, true)
  }

  const handleApply = () => {
    const unverified = draft.addresses.some(
      (address) => selection.addresses[address.id] && geocoding[address.id]?.status !== 'verified',
    )
    if (unverified) {
      setError('Confirm a Google location for every selected address.')
      return
    }
    onApply(draft, selection)
    onClose()
  }

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoAwesomeOutlinedIcon color="info" />
        AI Prefill
      </DialogTitle>
      {loading && <LinearProgress color="info" />}
      <DialogContent dividers>
        <MDTypography variant="button" color="text" fontWeight="regular" display="block" mb={2}>
          Customer notes and images are sent to Rivio&apos;s backend AI service for extraction. Review every suggestion before applying it.
        </MDTypography>

        <TextField
          fullWidth
          multiline
          minRows={5}
          label="Customer notes"
          value={text}
          onChange={(event) => setText(event.target.value)}
          disabled={loading}
        />

        <MDBox mt={2} display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <MDButton variant="outlined" color="info" component="label" disabled={loading || files.length >= MAX_FILES}>
            Add images
            <input hidden multiple type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleFiles} />
          </MDButton>
          {files.map((file, index) => (
            <Chip
              key={`${file.name}-${file.lastModified}-${index}`}
              label={file.name}
              onDelete={loading ? undefined : () => setFiles((current) => current.filter((_, i) => i !== index))}
            />
          ))}
          <MDTypography variant="caption" color="text">
            Up to {MAX_FILES} images, 10 MB each
          </MDTypography>
        </MDBox>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        {draft && (
          <MDBox mt={3}>
            <Alert severity={draft.warnings.length || draft.missingRequiredFields.length ? 'warning' : 'info'}>
              Confidence {Math.round(draft.confidence * 100)}% · Request {draft.requestId || 'unavailable'}
              {draft.missingRequiredFields.length > 0 && (
                <MDBox component="span" display="block">Missing: {draft.missingRequiredFields.join(', ')}</MDBox>
              )}
              {draft.warnings.map((warning) => (
                <MDBox component="span" display="block" key={warning}>{warning}</MDBox>
              ))}
            </Alert>

            <MDTypography variant="h6" mt={3}>Customer</MDTypography>
            {Object.entries(draft.entity).filter(([, value]) => value !== '' && value != null).length === 0 && (
              <MDTypography variant="button" color="text">No customer fields found.</MDTypography>
            )}
            {Object.entries(draft.entity).map(([field, value]) =>
              value !== '' && value != null ? (
                <MDBox key={field}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(selection.entity[field])}
                        onChange={(event) => toggle('entity', field, event.target.checked)}
                      />
                    }
                    label={`${ENTITY_LABELS[field] ?? field}: ${labelFor(field, value, dropdowns)}`}
                  />
                  {currentState.entity?.[field] && (
                    <MDTypography variant="caption" color="warning" ml={4} display="block">
                      Current: {labelFor(field, currentState.entity[field], dropdowns)}. Checking will overwrite it.
                    </MDTypography>
                  )}
                </MDBox>
              ) : null,
            )}

            <MDTypography variant="h6" mt={3}>Addresses</MDTypography>
            {draft.addresses.length === 0 && (
              <MDTypography variant="button" color="text">No address found.</MDTypography>
            )}
            {draft.addresses.map((address) => {
              const location = geocoding[address.id] || {}
              return (
                <MDBox
                  key={address.id}
                  mt={1}
                  p={2}
                  sx={({ palette, functions: { rgba } }) => ({ border: `1px solid ${rgba(palette.text.main, 0.15)}`, borderRadius: '8px' })}
                >
                  <MDTypography variant="button" fontWeight="bold">
                    {[address.street_address1, address.city, address.state, address.postal_code].filter(Boolean).join(', ') || 'Unparsed address'}
                  </MDTypography>
                  {location.candidates?.length > 0 && (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Confirmed Google location"
                      value={location.selectedPlaceId || ''}
                      onChange={(event) => handleAddressCandidate(address.id, event.target.value)}
                      sx={{ mt: 1.5 }}
                    >
                      {location.candidates.map((candidate) => (
                        <MenuItem key={candidate.placeId} value={candidate.placeId}>
                          {candidate.formattedAddress}{candidate.partialMatch ? ' (partial match)' : ''}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  {location.status === 'failed' && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Google could not find this address. It can&apos;t be applied from here. Enter it in the Address step.
                    </Alert>
                  )}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(selection.addresses[address.id])}
                        disabled={location.status !== 'verified'}
                        onChange={(event) => toggle('addresses', address.id, event.target.checked)}
                      />
                    }
                    label={
                      location.status === 'verified'
                        ? `Apply address and coordinates (${Number(address.latitude).toFixed(5)}, Number(address.longitude).toFixed(5)})`
                        : 'Confirm a location before applying'
                    }
                  />
                </MDBox>
              )
            })}

            <MDTypography variant="h6" mt={3}>Contacts</MDTypography>
            {draft.contacts.length === 0 && (
              <MDTypography variant="button" color="text">No contacts found.</MDTypography>
            )}
            {draft.contacts.map((contact) => {
              return (
                <MDBox key={contact.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(selection.contacts[contact.id])}
                        onChange={(event) => toggle('contacts', contact.id, event.target.checked)}
                      />
                    }
                    label={[`${contact.contact_first_name} ${contact.contact_last_name}`.trim(), contact.email, contact.phone]
                      .filter(Boolean)
                      .join(' · ') || 'Unnamed contact'}
                  />
                  {!contact.contact_type_id && (
                    <MDTypography variant="caption" color="warning" ml={4} display="block">
                      Contact type must be chosen in the Contacts step.
                    </MDTypography>
                  )}
                </MDBox>
              )
            })}
          </MDBox>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </MDButton>
        {!draft ? (
          <MDButton variant="gradient" color="info" onClick={handleGenerate} disabled={!canGenerate}>
            Generate draft
          </MDButton>
        ) : (
          <>
            <MDButton variant="outlined" color="info" onClick={handleGenerate} disabled={loading || !canGenerate}>
              Try again
            </MDButton>
            <MDButton variant="gradient" color="info" onClick={handleApply} disabled={loading}>
              Apply selected
            </MDButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
